import { useEffect, useRef, useState, type ReactNode } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { AccordionContent, AccordionItem } from "@/shared/ui/accordion";
import { Check, ChevronDown, Copy, Sparkles } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { toast } from "@/shared/hooks/use-toast";
import { SectionErrorBoundary } from "../SectionErrorBoundary";

type ChatTurnItemProps = {
  id: string;
  question: string;
  /** True while the turn's stream is in flight — pulses the sparkle accent. */
  running: boolean;
  /** Panel body — the turn's StatusBanner + answer content. */
  children: ReactNode;
};

/**
 * One chat turn as an accordion panel — the DashboardSurface PanelItem shell
 * anatomy (ruling 40 R2) lifted for intel-chat: question in the header as a
 * click-to-copy control, copy button with the 1.5s Check swap, chevron
 * trigger. No pin (ruling: pin dropped) and no patch chips — PanelItem itself
 * stays coupled to Panel/DashboardSpec, so this is a local lift, not a reuse.
 */
export function ChatTurnItem({ id, question, running, children }: ChatTurnItemProps) {
  const [copied, setCopied] = useState(false);
  const copiedResetRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const displayQuestion = question.charAt(0).toUpperCase() + question.slice(1);

  useEffect(() => {
    return () => {
      if (copiedResetRef.current) clearTimeout(copiedResetRef.current);
    };
  }, []);

  const copyQuestion = async () => {
    try {
      await navigator.clipboard.writeText(question);
      setCopied(true);
      if (copiedResetRef.current) clearTimeout(copiedResetRef.current);
      copiedResetRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({
        title: "Copy failed",
        description: "Could not copy the question to the clipboard.",
        variant: "destructive",
      });
    }
  };

  return (
    <AccordionItem
      id={`turn-${id}`}
      value={id}
      className="overflow-hidden rounded-xl border border-border bg-card/40 shadow-sm"
    >
      <AccordionPrimitive.Header className="flex items-center gap-1 px-5 py-3 pr-3">
        {/* Assistant accent (ticket 45 round 2 — the old Dashboard2Chat glyph):
            pulses while the run streams, settles static as the turn's marker.
            CSS-class animation, so it carries the motion-reduce guard itself. */}
        <Sparkles
          data-testid="turn-sparkle"
          aria-hidden
          className={cn(
            "mr-1.5 h-4 w-4 shrink-0 text-primary",
            running && "animate-pulse motion-reduce:animate-none",
          )}
        />
        <span
          role="button"
          tabIndex={0}
          title="Click to copy question"
          className="min-w-0 flex-1 cursor-text select-text truncate text-left text-[18px] font-medium text-foreground"
          onClick={() => void copyQuestion()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              void copyQuestion();
            }
          }}
        >
          {displayQuestion}
        </span>
        <button
          type="button"
          onClick={() => void copyQuestion()}
          aria-label={copied ? "Question copied" : "Copy question"}
          title={copied ? "Copied" : "Copy question"}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground"
        >
          {copied ? (
            <Check className="h-4 w-4 text-primary" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
        <AccordionPrimitive.Trigger
          data-accordion-trigger
          aria-label="Toggle answer"
          className="group shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground [&[data-state=open]>svg.chev]:rotate-180"
        >
          <ChevronDown className="chev h-4 w-4 transition-transform duration-200" />
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionContent className="px-5">
        <div className="space-y-3">
          <SectionErrorBoundary label="answer">{children}</SectionErrorBoundary>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
